import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Alert,
    useTheme,
    TextField,
    InputAdornment,
    Pagination,
    IconButton,
    Chip,
    Tooltip,
    Card,
    CardContent,
    Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import api from '../services/api';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatDistanceToNow } from 'date-fns';

const DeletedTransactions = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const theme = useTheme();
    const [filter, setFilter] = useState('');
    const [page, setPage] = useState(1);
    const rowsPerPage = 10;
    const { showToast } = useToast();

    const fetchDeletedTransactions = async () => {
        setLoading(true);
        try {
            const data = await api.dashboard.getDeletedTransactions();
            setTransactions(Array.isArray(data) ? data : []);
            setError('');
        } catch (err) {
            console.error('Error fetching deleted transactions:', err);
            setError(err.message || 'Error fetching deleted transactions');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDeletedTransactions();
    }, []);

    const handleRefresh = () => {
        fetchDeletedTransactions();
        showToast('Refreshing data...', 'info');
    };

    const filteredTransactions = transactions.filter((tr) =>
        (tr.reference?.toLowerCase() || '').includes(filter.toLowerCase()) ||
        (tr.username?.toLowerCase() || '').includes(filter.toLowerCase()) ||
        (tr.email?.toLowerCase() || '').includes(filter.toLowerCase()) ||
        (tr.description?.toLowerCase() || '').includes(filter.toLowerCase())
    );

    const paginatedTransactions = filteredTransactions.slice(
        (page - 1) * rowsPerPage,
        page * rowsPerPage
    );

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-NG', {
            style: 'currency',
            currency: 'NGN',
        }).format(amount || 0);
    };

    if (loading && transactions.length === 0) {
        return <LoadingSpinner />;
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                <Box>
                    <Typography variant="h4" fontWeight={700} sx={{ color: 'primary.main', mb: 1 }}>
                        Deleted Transactions
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        View and manage transactions that have been soft-deleted from the system.
                    </Typography>
                </Box>
                <IconButton onClick={handleRefresh} sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                    <RefreshIcon color="primary" />
                </IconButton>
            </Stack>

            {error && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                    {error}
                </Alert>
            )}

            <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                <CardContent sx={{ p: 3 }}>
                    <TextField
                        fullWidth
                        placeholder="Search by reference, username, or description..."
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon color="action" />
                                </InputAdornment>
                            ),
                            sx: { borderRadius: 2 }
                        }}
                    />
                </CardContent>
            </Card>

            <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <Table>
                    <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>User</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Reference</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Description</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Deleted At</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedTransactions.length > 0 ? (
                            paginatedTransactions.map((tr) => (
                                <TableRow key={tr._id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={600}>{tr.username || 'N/A'}</Typography>
                                        <Typography variant="caption" color="text.secondary">{tr.email}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{tr.reference}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight={700} color={tr.trnxType === 'credit' ? 'success.main' : 'error.main'}>
                                            {tr.trnxType === 'credit' ? '+' : '-'}{formatCurrency(tr.amount)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={tr.status}
                                            size="small"
                                            color={
                                                tr.status === 'success' || tr.status === 'completed'
                                                    ? 'success'
                                                    : tr.status === 'failed'
                                                        ? 'error'
                                                        : 'warning'
                                            }
                                            sx={{ fontWeight: 600, textTransform: 'capitalize' }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                            {tr.description}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">
                                            {tr.deletedAt ? new Date(tr.deletedAt).toLocaleString() : 'N/A'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {tr.deletedAt ? formatDistanceToNow(new Date(tr.deletedAt), { addSuffix: true }) : ''}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                                    <Box sx={{ opacity: 0.5 }}>
                                        <DeleteIcon sx={{ fontSize: 48, mb: 1 }} />
                                        <Typography>No deleted transactions found</Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {filteredTransactions.length > rowsPerPage && (
                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
                    <Pagination
                        count={Math.ceil(filteredTransactions.length / rowsPerPage)}
                        page={page}
                        onChange={(e, v) => setPage(v)}
                        color="primary"
                        size="large"
                    />
                </Box>
            )}
        </Box>
    );
};

export default DeletedTransactions;
